'use client'

import {useRef, useState} from 'react'
import {Bot, MessageSquare, Settings, Store, User, Users} from 'lucide-react'
import type {LucideIcon} from 'lucide-react'
import type {MenuKey} from '@/lib/constants'
import type {AccountItem} from '@/hooks/useAccounts'
import {AccountSwitcherPopover} from '@/components/AccountSwitcherPopover'

type PrimaryRailProps = {
    avatarUrl: string
    activeMenu: MenuKey
    isLoggedIn: boolean
    conversationUnreadCount: number
    accountUnreadCounts: Record<string, number>
    accounts: AccountItem[]
    currentWxid: string | undefined
    onNavigate: (path: string) => void
    onSwitchAccount: (wxid: string) => void
    onRefreshProfile: () => Promise<void> | void
    onManageAccounts: () => void
}

type NavEntry = {
    key: MenuKey
    label: string
    path: string
    Icon: LucideIcon
    size?: number
}

const TOP_NAV: NavEntry[] = [
    {key: 'conversation', label: '聊天会话', path: '/conversation/realtime', Icon: MessageSquare},
    {key: 'message', label: '自动化', path: '/message/auto-reply', Icon: Bot},
    {key: 'contact', label: '好友管理', path: '/contact/address-book', Icon: User},
    {key: 'group', label: '群聊管理', path: '/group/welcome', Icon: Users},
    {key: 'plugin', label: '插件', path: '/plugin/market', Icon: Store},
]

const SETTINGS_NAV: NavEntry = {
    key: 'settings',
    label: '设置',
    path: '/settings/personal',
    Icon: Settings,
    size: 22,
}

export function PrimaryRail({
    avatarUrl,
    activeMenu,
    isLoggedIn,
    conversationUnreadCount,
    accountUnreadCounts,
    accounts,
    currentWxid,
    onNavigate,
    onSwitchAccount,
    onRefreshProfile,
    onManageAccounts,
}: PrimaryRailProps) {
    const visibleTopNav = TOP_NAV
    const avatarBtnRef = useRef<HTMLButtonElement | null>(null)
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)

    function handleAvatarClick() {
        if (popoverOpen) {
            setPopoverOpen(false)
            return
        }
        const rect = avatarBtnRef.current?.getBoundingClientRect()
        if (rect) {
            setAnchorRect(rect)
            setPopoverOpen(true)
        }
    }

    function handleSwitch(wxid: string) {
        setPopoverOpen(false)
        onSwitchAccount(wxid)
    }

    function handleManageAccounts() {
        setPopoverOpen(false)
        onManageAccounts()
    }

    return (
        <aside className="hidden md:flex w-[60px] h-full bg-rail flex-col items-center py-6 text-railIcon shrink-0">
            <button
                ref={avatarBtnRef}
                type="button"
                className="mb-8 relative group"
                onClick={handleAvatarClick}
                title={isLoggedIn ? '切换账号' : '选择账号'}
            >
                {isLoggedIn ? (
                    <img
                        src={avatarUrl}
                        alt="profile"
                        className="w-10 h-10 rounded border border-gray-600 group-hover:border-gray-400 bg-gray-700"
                    />
                ) : (
                    <span
                        className="w-10 h-10 rounded border border-gray-600 group-hover:border-gray-400 bg-gray-700 flex items-center justify-center text-green-400"
                        aria-hidden
                    >
                        <Bot size={22}/>
                    </span>
                )}
                {isLoggedIn ? (
                    <span
                        aria-hidden
                        className="absolute top-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-rail"
                    />
                ) : null}
            </button>

            {popoverOpen && (
                <AccountSwitcherPopover
                    anchorRect={anchorRect}
                    accounts={accounts}
                    accountUnreadCounts={accountUnreadCounts}
                    currentWxid={currentWxid}
                    onSwitch={handleSwitch}
                    onRefreshProfile={onRefreshProfile}
                    onManageAccounts={handleManageAccounts}
                    onClose={() => setPopoverOpen(false)}
                />
            )}

            <nav className="flex-1 w-full flex flex-col items-center gap-4">
                {visibleTopNav.map(({key, label, path, Icon, size}) => {
                    const active = activeMenu === key
                    return (
                        <button
                            key={key}
                            type="button"
                            className={`w-full flex justify-center py-3 transition-colors ${
                                active ? 'text-accent' : 'hover:text-gray-200'
                            }`}
                            onClick={() => onNavigate(path)}
                            title={label}
                            aria-label={label}
                        >
                            <span className="relative">
                                <Icon size={size ?? 24}/>
                                {key === 'conversation' && conversationUnreadCount > 0 ? (
                                    <span className="absolute -right-2 -top-2 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-[10px] leading-4 text-white">
                                        {conversationUnreadCount > 99 ? '99+' : conversationUnreadCount}
                                    </span>
                                ) : null}
                            </span>
                        </button>
                    )
                })}
            </nav>

            <button
                type="button"
                className={`w-full flex justify-center py-3 transition-colors ${
                    activeMenu === SETTINGS_NAV.key ? 'text-accent' : 'hover:text-gray-200'
                }`}
                onClick={() => onNavigate(SETTINGS_NAV.path)}
                title={SETTINGS_NAV.label}
                aria-label={SETTINGS_NAV.label}
            >
                <SETTINGS_NAV.Icon size={SETTINGS_NAV.size ?? 22}/>
            </button>
        </aside>
    )
}
